/**
 * State Seal CRM Opportunity Pipeline API (separate from REACT_APP_BACKEND_URL).
 * Base URL should resolve to .../api/v1 (with or without trailing slash).
 */

const getPipelineBaseUrl = () => {
  const raw = process.env.REACT_APP_STATESEAL_API_URL || '';
  return raw.replace(/\/$/, '');
};

/**
 * URLSearchParams.toString() encodes spaces as `+` (x-www-form-urlencoded).
 * Use `%20` instead so salesperson names like "Fields, Elizabeth / Out" stay consistent
 * with other pipeline GETs and are not mangled (e.g. `+` → `%2B`) by intermediaries.
 */
const formUrlEncodedQueryString = (params) => params.toString().replace(/\+/g, '%20');

/**
 * Shared query for pipeline GETs. Optional dimensions match State Seal backend (exact DB strings).
 * Uses `prob` and `type` (not `probability` / `customer_type`) when both aliases exist per API contract.
 * Multi-value filters use repeated params (?stage=A&stage=B) — backend does not split on commas.
 * @param {Record<string, string|string[]|undefined>} filters — camelCase keys from the UI layer
 */
export const buildPipelineQuery = (filters = {}) => {
  const params = new URLSearchParams();
  const {
    snapshotDate,
    salesperson,
    company,
    dateFrom,
    dateTo,
    status,
    stage,
    prob,
    type,
  } = filters;

  if (snapshotDate) {
    params.set('snapshot_date', snapshotDate);
  }
  if (dateFrom) {
    params.set('date_from', dateFrom);
  }
  if (dateTo) {
    params.set('date_to', dateTo);
  }

  // Multi-value filters: arrays send repeated params; plain strings (e.g. chart-click) still work.
  const appendMulti = (key, val) => {
    if (!val) return;
    (Array.isArray(val) ? val : [val]).forEach((v) => {
      if (v) params.append(key, String(v).trim());
    });
  };

  appendMulti('salesperson', salesperson);
  appendMulti('company', company);
  appendMulti('status', status);
  appendMulti('stage', stage);
  appendMulti('prob', prob);
  appendMulti('type', type);

  const qs = formUrlEncodedQueryString(params);
  return qs ? `?${qs}` : '';
};

const joinUrl = (path) => {
  const base = getPipelineBaseUrl();
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${base}${p}`;
};

/**
 * Same behavior as apiClient: Helios admins impersonating a tenant send the effective customer id.
 * Header name matches admin-backend / FastAPI conventions (case-insensitive for fetch).
 */
const getImpersonationHeaders = () => {
  const headers = {};
  const savedState = sessionStorage.getItem('impersonation_state');
  if (savedState) {
    try {
      const state = JSON.parse(savedState);
      if (state.isImpersonating && state.currentCustomer?.id) {
        headers['X-Impersonated-Customer-ID'] = state.currentCustomer.id;
      }
    } catch (e) {
      console.error('Could not parse impersonation state from sessionStorage', e);
    }
  }
  return headers;
};

/** Parse FastAPI-style JSON `{ "detail": "..." }` or plain text error bodies */
const parseErrorBody = (rawText, status, statusText) => {
  if (!rawText?.trim()) {
    return statusText || 'Unknown error';
  }
  try {
    const body = JSON.parse(rawText);
    if (typeof body.detail === 'string') return body.detail;
    if (Array.isArray(body.detail)) {
      return body.detail.map((d) => d.msg || d).join('; ');
    }
    if (typeof body.message === 'string') return body.message;
    if (typeof body.error === 'string') return body.error;
  } catch {
    /* plain text */
  }
  return rawText.trim();
};

/**
 * @param {string} path - e.g. '/state-kpis'
 * @param {object} options
 * @param {() => Promise<string>} options.getAccessTokenSilently
 * @param {Record<string, string|undefined>} [options.queryFilters]
 */
export const pipelineGet = async (path, { getAccessTokenSilently, queryFilters } = {}) => {
  const base = getPipelineBaseUrl();
  if (!base) {
    throw new Error('REACT_APP_STATESEAL_API_URL is not configured.');
  }

  const qs = queryFilters ? buildPipelineQuery(queryFilters) : '';
  const url = `${joinUrl(path)}${qs}`;

  const token = await getAccessTokenSilently({
    audience: process.env.REACT_APP_AUTH0_AUDIENCE,
    scope: 'openid profile email',
  });

  const res = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      ...getImpersonationHeaders(),
    },
  });

  const responseText = await res.text();

  if (!res.ok) {
    const detail = parseErrorBody(responseText, res.status, res.statusText);
    const err = new Error(`Pipeline API error: ${res.status} — ${detail}`);
    err.status = res.status;
    err.detail = detail;
    throw err;
  }

  if (!responseText) return null;
  try {
    return JSON.parse(responseText);
  } catch {
    return responseText;
  }
};

export const fetchStateHealth = (auth) => pipelineGet('/state-health', auth);
export const fetchStateSnapshots = (auth) => pipelineGet('/state-snapshots', auth);
export const fetchStateSalespersons = (auth, queryFilters) =>
  pipelineGet('/state-salespersons', { ...auth, queryFilters });
export const fetchStateCompanies = (auth, queryFilters) =>
  pipelineGet('/state-companies', { ...auth, queryFilters });
export const fetchStateKpis = (auth, queryFilters) =>
  pipelineGet('/state-kpis', { ...auth, queryFilters });
export const fetchPipelineByStage = (auth, queryFilters) =>
  pipelineGet('/state-pipeline/by-stage', { ...auth, queryFilters });
export const fetchPipelineBySalesperson = (auth, queryFilters) =>
  pipelineGet('/state-pipeline/by-salesperson', { ...auth, queryFilters });
export const fetchPipelineByCustomerType = (auth, queryFilters) =>
  pipelineGet('/state-pipeline/by-customer-type', { ...auth, queryFilters });
export const fetchPipelineByCloseMonth = (auth, queryFilters) =>
  pipelineGet('/state-pipeline/by-close-month', { ...auth, queryFilters });

export const fetchStateOpportunities = async (auth, queryFilters, { limit = 500, offset = 0, search = '' } = {}) => {
  const params = new URLSearchParams();
  if (queryFilters) {
    const q = buildPipelineQuery(queryFilters).replace(/^\?/, '');
    if (q) {
      // Parse with URLSearchParams so `+` in the query means space (decodeURIComponent alone does not).
      const fromFilters = new URLSearchParams(q);
      fromFilters.forEach((value, key) => {
        params.append(key, value);
      });
    }
  }
  params.set('limit', String(limit));
  params.set('offset', String(offset));
  if (search) params.set('search', search);

  const path = `/state-opportunities?${formUrlEncodedQueryString(params)}`;
  return pipelineGet(path, auth);
};

export const fetchSyteLineConfig = (auth) => pipelineGet('/state-config/syteline', auth);

export const asArray = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.results)) return payload.results;
  /** e.g. GET /state-snapshots → `{ "snapshots": [ ... ] }` */
  if (Array.isArray(payload.snapshots)) return payload.snapshots;
  return [];
};

export { getPipelineBaseUrl };
